/**
 * Game state: one plain, JSON-serializable object (G). All mutation goes through
 * functions in rules/. RNG state lives IN G so saves replay identically.
 * Shape ported from legacy/admirals-return.html newGameState().
 */
import { makeRng, type Rng } from './rng';
import { genMap } from './mapgen';

export type Side = 'R' | 'Y';
/** FT (Fort) is an immobile (move 0) unit built near cities (manual: damage_chart). */
export type ShipType = 'CV' | 'BB' | 'CR' | 'SS' | 'DD' | 'PT' | 'AT' | 'TR' | 'FT';
export type Tile = 'water' | 'land';
export type MapSize = 'small' | 'medium' | 'large';

export const SIZES: Record<MapSize, { W: number; H: number; n: number; label: string }> = {
  small: { W: 16, H: 11, n: 9, label: 'Small (16×11, 9 cities)' },
  medium: { W: 21, H: 14, n: 14, label: 'Medium (21×14, 14 cities)' },
  large: { W: 26, H: 17, n: 18, label: 'Large (26×17, 18 cities)' },
};

export interface City {
  c: number;
  r: number;
  name: string;
  /** points paid into the owner's treasury each scoring turn: 200–500. A city
   *  worth exactly 200 doubles as a shipyard (units can be purchased there). */
  vp: number;
  owner: Side | null;
  home: Side | null;
}

export interface Ship {
  id: number;
  side: Side;
  type: ShipType;
  c: number;
  r: number;
  hp: number;
  moved: number;
  done: boolean;
  submerged: boolean;
  /** fought last combat round → visible through fog (manual: game-rules) */
  fought: boolean;
  /** anchored in place this turn (manual: D key) */
  anchored: boolean;
  /** optional task-force destination [c,r] assigned on the buy screen (WP-11
   *  destinations recap). Planning aid only — does not affect rules. */
  dest?: [number, number];
}

export interface GameState {
  schema: 1;
  seed: number;
  /** live PRNG state — advance only via stateRng(G) */
  rngState: number;
  W: number;
  H: number;
  nCities: number;
  /** tiles[r][c] */
  tiles: Tile[][];
  cities: City[];
  ships: Ship[];
  nextId: number;
  turn: number;
  maxTurn: number;
  phase: 'setup' | 'move' | 'combat' | 'over';
  active: Side;
  /** the single currency: each side's points pool. Cities pay into it every
   *  scoring turn; buying/building ships spends from it; the winner is decided
   *  by it (see finishGame). Seeded with a starting endowment (see newGameOpts). */
  vp: Record<Side, number>;
  /** required win margin % for the favored side */
  ratio: number;
  favored: Side;
  winner: Side | null;
}

export interface NewGameOpts {
  size?: MapSize;
  seed: number;
  maxTurn?: number;
  /** starting points endowment per side (default 200) — the buy screen spends
   *  from it, and whatever's left carries as the opening score. */
  budgetR?: number;
  budgetY?: number;
  forceRatio?: number;
  forceFavored?: Side;
}

/** Rng view bound to G — every draw persists back into G.rngState. */
export function stateRng(g: GameState): Rng {
  const inner = makeRng(g.rngState);
  const sync = <T>(v: T): T => {
    g.rngState = inner.state();
    return v;
  };
  return {
    next: () => sync(inner.next()),
    int: (n) => sync(inner.int(n)),
    pick: (arr) => sync(inner.pick(arr)),
    state: () => inner.state(),
  };
}

export function newGameState(opts: NewGameOpts): GameState {
  const sz = SIZES[opts.size ?? 'medium'];
  const g: GameState = {
    schema: 1,
    seed: opts.seed,
    rngState: opts.seed >>> 0,
    W: sz.W,
    H: sz.H,
    nCities: sz.n,
    tiles: [],
    cities: [],
    ships: [],
    nextId: 1,
    turn: 1,
    maxTurn: 0,
    phase: 'setup',
    active: 'R',
    vp: { R: opts.budgetR ?? 200, Y: opts.budgetY ?? 200 },
    ratio: 0,
    favored: 'R',
    winner: null,
  };
  const rng = stateRng(g);
  g.maxTurn = opts.maxTurn ?? 14 + rng.int(5);
  genMap(g, rng);
  if (opts.forceRatio != null) {
    g.ratio = opts.forceRatio;
    g.favored = opts.forceFavored ?? 'R';
  }
  return g;
}

export function serialize(g: GameState): string {
  return JSON.stringify(g);
}

export function deserialize(json: string): GameState {
  const g = JSON.parse(json) as GameState;
  if (g.schema !== 1) throw new Error(`unknown save schema ${String(g.schema)}`);
  return g;
}

export function cityAt(g: GameState, c: number, r: number): City | undefined {
  return g.cities.find((x) => x.c === c && x.r === r);
}

export function shipsAt(g: GameState, c: number, r: number): Ship[] {
  return g.ships.filter((s) => s.c === c && s.r === r);
}
